'use client';

import { c as _c } from "react/compiler-runtime";
import { EntityPlacementCtaTypeEnum } from '@cc-shopping/contracts/cross-site/marketplace/enums';
import { useIsHydrated } from '@cc-shopping/hooks/useIsHydrated';
import { useAnalyticsPageView } from '@cc-shopping/platform-analytics/AnalyticsPageView/Context';
import { useAnalyticsSkuViewedContext } from '@cc-shopping/platform-analytics/AnalyticsSkuViewed/Context';
import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { CTASearchParams } from '@cc-shopping/platform-analytics/lib/ctaSearchParams';
import { FinishType } from '@cc-shopping/platform-analytics/types';
import type { transformCreditCardsMultiCompareEntity } from '@cc-shopping/structured-content-renderer/hydration/CreditCardsMultiCompare/transform';
import { theme } from '@cc-shopping/ui/theme';
import { Box, Link, Typography } from '@mui/material';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { PrimaryLink } from 'src/features/product-card/ApplyNowButton';
import { CtaLink } from 'src/features/product-card/CTA/CtaLink';
import { RatesAndFeesLink } from 'src/features/product-card/RatesAndFeesLink';
import { CreditCardsMultiCompareRating } from './Rating';
import ResizingContainer from './ResizingContainer';
interface ProductCardProps {
  offer: ReturnType<typeof transformCreditCardsMultiCompareEntity>;
  disableMonetization: boolean;
  ctaType?: string;
  maxCtaHeight: number;
  setMaxCtaHeight: (height: number) => void;
}
export const ProductCard = props => {
  const $ = _c(77);
  const {
    offer,
    ctaType,
    disableMonetization,
    maxCtaHeight,
    setMaxCtaHeight
  } = props;
  const {
    skuImpressionId
  } = useAnalyticsSkuViewedContext();
  const {
    pageName
  } = useAnalyticsPageView();
  const path = usePathname();
  const isHydrated = useIsHydrated();
  let t0;
  if ($[0] !== skuImpressionId) {
    t0 = {
      actionName: "read_review",
      actionSection: "cc_multi_card_comparison",
      actionType: "select",
      additionalAttributes: {
        sku_impression_id: skuImpressionId
      }
    };
    $[0] = skuImpressionId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const fireReadReviewAction = usePageAction(t0);
  const isMonetizing = offer.details.isMonetizing && !disableMonetization;
  const isFallbackToReview = !(isMonetizing && offer.details.isActive && offer.applyLink);
  const showApplyNowButton = !isFallbackToReview && ctaType === "applyNow";
  let t1;
  bb0: {
    if (!isHydrated || !offer?.reviewLink) {
      t1 = false;
      break bb0;
    }
    let t2;
    if ($[2] !== offer.reviewLink) {
      t2 = new URL(offer.reviewLink);
      $[2] = offer.reviewLink;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const offerReviewURL = t2;
    t1 = path === offerReviewURL.pathname;
  }
  const reviewLinkIsSelfReferencing = t1;
  if (ctaType === "readReview" && !reviewLinkIsSelfReferencing) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        paddingTop: ".75rem"
      };
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== offer.analytics) {
      t3 = {
        ...offer.analytics,
        monetizable: "no",
        impressionPosition: offer.analytics.sectionPosition
      };
      $[5] = offer.analytics;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = [];
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== fireReadReviewAction) {
      t5 = () => {
        fireReadReviewAction();
      };
      $[8] = fireReadReviewAction;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== offer.reviewLink || $[11] !== t3 || $[12] !== t5) {
      t6 = <Box width="100%" mb={2} sx={t2}><CtaLink analytics={t3} type="APPLY_NOW_BUTTON" href={offer.reviewLink} Component={PrimaryLink} finishType={FinishType.InternalContent} parameterActions={t4} onClickAnalyticsCallback={t5}>READ REVIEW</CtaLink></Box>;
      $[10] = offer.reviewLink;
      $[11] = t3;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  const rel = offer.cta.type === EntityPlacementCtaTypeEnum.ApplyNow && !isFallbackToReview ? "nofollow" : undefined;
  const t2 = isFallbackToReview ? offer.reviewLink : offer.applyLink;
  const t3 = offer.cta.type === EntityPlacementCtaTypeEnum.ApplyNow && !isFallbackToReview ? "_blank" : "_self";
  let t4;
  if ($[14] !== offer.ctaAnalytics.section.position) {
    t4 = {
      name: "cc_multi_card_comparison",
      position: offer.ctaAnalytics.section.position
    };
    $[14] = offer.ctaAnalytics.section.position;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = !isFallbackToReview ? "yes_assumed" : "no";
  let t6;
  if ($[16] !== offer.ctaAnalytics || $[17] !== t4 || $[18] !== t5) {
    t6 = {
      ...offer.ctaAnalytics,
      section: t4,
      monetizable: t5,
      impressionPosition: offer.ctaAnalytics.productPosition
    };
    $[16] = offer.ctaAnalytics;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const t7 = !isFallbackToReview ? FinishType.ExternalApplication : FinishType.InternalContent;
  let t8;
  if ($[20] !== isFallbackToReview || $[21] !== offer.cta.type || $[22] !== offer.ctaAnalytics.productPosition || $[23] !== pageName) {
    t8 = offer.cta.type === EntityPlacementCtaTypeEnum.ApplyNow && !isFallbackToReview ? [CTASearchParams.MONETIZING, CTASearchParams.SHOPPING_FINISH, {
      replacements: {
        impression_page_name: pageName || window.location.pathname,
        impression_position: offer.ctaAnalytics.productPosition.toString()
      }
    }] : [];
    $[20] = isFallbackToReview;
    $[21] = offer.cta.type;
    $[22] = offer.ctaAnalytics.productPosition;
    $[23] = pageName;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== offer.image.medium.url) {
    t9 = <img src={offer.image.medium.url} height="auto" width="100%" alt="Card image" />;
    $[25] = offer.image.medium.url;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== rel || $[28] !== t2 || $[29] !== t3 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t10 = <CtaLink Component={Link} href={t2} rel={rel} target={t3} analytics={t6} type="PRODUCT_IMAGE" finishType={t7} parameterActions={t8}>{t9}</CtaLink>;
    $[27] = rel;
    $[28] = t2;
    $[29] = t3;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      textAlign: "center",
      display: "flex",
      justifyContent: "center"
    };
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== offer.name) {
    t12 = <Typography component="span" variant="textBold" sx={t11}>{offer.name}</Typography>;
    $[36] = offer.name;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t10 || $[39] !== t12) {
    t13 = <Box mb={2} width="100%">{t10}{t12}</Box>;
    $[38] = t10;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== offer || $[42] !== reviewLinkIsSelfReferencing) {
    t14 = offer.starRating && <Box mb={2} display="flex" justifyContent="center"><CreditCardsMultiCompareRating offer={offer} blockCTAClick={reviewLinkIsSelfReferencing} /></Box>;
    $[41] = offer;
    $[42] = reviewLinkIsSelfReferencing;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== offer.analytics || $[45] !== offer.applyLink || $[46] !== offer.institution || $[47] !== offer.phoneNumber || $[48] !== pageName || $[49] !== rel || $[50] !== showApplyNowButton) {
    t15 = showApplyNowButton && <Box width="100%" mb={1}><CtaLink analytics={{
        ...offer.analytics,
        section: {
          name: "cc_multi_card_comparison",
          position: offer.analytics.sectionPosition
        },
        monetizable: "yes_assumed",
        impressionPosition: offer.analytics.sectionPosition
      }} rel={rel} type="APPLY_NOW_BUTTON" href={offer.applyLink} Component={PrimaryLink} target="_blank" finishType={FinishType.ExternalApplication} parameterActions={[CTASearchParams.MONETIZING, CTASearchParams.SHOPPING_FINISH, {
        replacements: {
          impression_page_name: pageName || window.location.pathname,
          impression_position: offer.analytics.sectionPosition.toString()
        }
      }]}>APPLY NOW{" "}<span aria-hidden="true" className="mask-icon mask-icon-open-in-new" style={{
          width: "1rem",
          height: "1rem"
        }} /></CtaLink>{offer.institution?.name && <Typography mt={2} sx={_temp}>On {offer.institution.name}’s website</Typography>}{offer.institution?.name && offer.phoneNumber ? <Box sx={_temp2}>or call{" "}<a style={{
          color: theme.palette.secondary.main
        }} href={`tel:+${offer.phoneNumber}`}>{offer.phoneNumber}</a></Box> : null}</Box>;
    $[44] = offer.analytics;
    $[45] = offer.applyLink;
    $[46] = offer.institution;
    $[47] = offer.phoneNumber;
    $[48] = pageName;
    $[49] = rel;
    $[50] = showApplyNowButton;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  let t16;
  if ($[52] !== fireReadReviewAction || $[53] !== offer.analytics || $[54] !== offer.details.reviewLink || $[55] !== offer.reviewLink || $[56] !== reviewLinkIsSelfReferencing || $[57] !== showApplyNowButton) {
    t16 = !showApplyNowButton && offer.details.reviewLink && !reviewLinkIsSelfReferencing && <Box width="100%" mb={2}><CtaLink analytics={{
        ...offer.analytics,
        section: {
          name: "cc_multi_card_comparison",
          position: offer.analytics.sectionPosition
        },
        monetizable: "no",
        impressionPosition: offer.analytics.sectionPosition
      }} type="APPLY_NOW_BUTTON" href={offer.reviewLink} Component={PrimaryLink} finishType={FinishType.InternalContent} parameterActions={[]} onClickAnalyticsCallback={() => {
        fireReadReviewAction();
      }}>READ REVIEW</CtaLink></Box>;
    $[52] = fireReadReviewAction;
    $[53] = offer.analytics;
    $[54] = offer.details.reviewLink;
    $[55] = offer.reviewLink;
    $[56] = reviewLinkIsSelfReferencing;
    $[57] = showApplyNowButton;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== isMonetizing || $[60] !== offer.analytics || $[61] !== offer.displayName || $[62] !== offer.name || $[63] !== offer.termsAndFeesLink || $[64] !== offer.versionId || $[65] !== pageName) {
    t17 = isMonetizing && offer.termsAndFeesLink && <Box width="100%" display="flex" justifyContent="center"><RatesAndFeesLink analytics={{
        ...offer.analytics,
        section: {
          name: "cc_multi_card_comparison",
          position: offer.analytics.sectionPosition
        },
        monetizable: "yes_assumed",
        impressionPosition: offer.analytics.sectionPosition
      }} href={offer.termsAndFeesLink} versionId={offer.versionId} name={offer.displayName || offer.name} additionalParams={{
        impression_page_name: pageName || window.location.pathname,
        impression_position: offer.analytics.sectionPosition.toString()
      }}>{"Rates & Fees"}</RatesAndFeesLink></Box>;
    $[59] = isMonetizing;
    $[60] = offer.analytics;
    $[61] = offer.displayName;
    $[62] = offer.name;
    $[63] = offer.termsAndFeesLink;
    $[64] = offer.versionId;
    $[65] = pageName;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  let t18;
  if ($[67] !== maxCtaHeight || $[68] !== setMaxCtaHeight || $[69] !== t14 || $[70] !== t15 || $[71] !== t16 || $[72] !== t17) {
    t18 = <ResizingContainer maxCtaHeight={maxCtaHeight} setMaxCtaHeight={setMaxCtaHeight}>{t14}{t15}{t16}{t17}</ResizingContainer>;
    $[67] = maxCtaHeight;
    $[68] = setMaxCtaHeight;
    $[69] = t14;
    $[70] = t15;
    $[71] = t16;
    $[72] = t17;
    $[73] = t18;
  } else {
    t18 = $[73];
  }
  let t19;
  if ($[74] !== t13 || $[75] !== t18) {
    t19 = <Box display="flex" alignItems="center" justifyContent="space-between" flexDirection="column" height="100%" gap=".25rem">{t13}{t18}</Box>;
    $[74] = t13;
    $[75] = t18;
    $[76] = t19;
  } else {
    t19 = $[76];
  }
  return t19;
};
function _temp(theme) {
  return {
    fontSize: "0.75rem",
    color: theme.palette.grey[500],
    textAlign: "center"
  };
}
function _temp2(theme_0) {
  return {
    fontSize: "0.75rem",
    color: theme_0.palette.grey[500],
    textAlign: "center"
  };
}